import React, { useState, useRef, useEffect, useMemo } from 'react';
import { formatBrandMoney, formatBrandMoneyCompact, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  buildBankSelectOptionsFromAccounts,
  buildPaymentBeneficiaryOptions,
  SAUDI_PAYMENT_ORDER_METHODS,
  SAUDI_PURPOSE_CODES,
  SAUDI_PAYMENT_PRIORITIES,
  SAUDI_SAMPLE_IBAN,
  validateSaudiIban,
} from './bankingSaudiHelpers';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileCheck,
  Plus,
  Calendar,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  RefreshCcw,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Banknote,
  X,
  Layers,
  Shield,
  Users,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import localApiAdapter from '../../core/services/localApiAdapter';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { mapPaymentOrderFromApi, mapPaymentOrderToApi } from './bankingApiMappers';
import { exportBankingRows, exportBankingPdf, importBankingRows } from './bankingExportImport';
import { resolveBankingModalFormTabs } from './bankingHelpers';
import { normalizeAndSyncBankAccounts } from './bankingTreasurySync';
import { useTranslation } from 'react-i18next';
import '../../shared/styles/GPSTrackingDatePicker.css';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import applyColumnFilters, { textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import { useClients } from '../../core/contexts/ClientsContext';
import { useBanqueConfig, useBanqueListViewMode, sliceBanqueListItems } from './useBanqueConfig';

const PaymentOrdersPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.paymentOrdersPage.${key}`, opts);
  const fmt = (value) => formatBrandMoney(value);
  const dateLocale = getBrandLocale(i18n.language);
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { clients } = useClients();
  const bankingConfig = useBanqueConfig();
  const entityLabel = p('entity_label', { defaultValue: 'أمر الدفع' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'أوامر الدفع' });
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useBanqueListViewMode(bankingConfig);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // Filters
  const [mainViewMode, setMainViewMode] = useState('all_orders');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  
  const { filters: columnFilters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [columnVisibility, setColumnVisibility] = useState({
    reference: true,
    date: true,
    beneficiaire: true,
    banque: true,
    montant: true,
    dateExecution: true,
  });

  const [orderFormTab, setOrderFormTab] = useState('generalInfo');
  
  // Data - NO AUTO-SELECTION - all fields empty strings
  const [orders, setOrders] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    reference: '',
    beneficiaire: '',
    banque: '',
    montant: '',
    motif: '',
    dateExecution: '',
    status: '',
    account_id: '',
    beneficiary_account: '',
    paymentMethod: 'sarie',
    purposeCode: '02',
    priority: 'normal',
  });
  
  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    loadBankAccounts();
    loadSuppliers();
    loadOrders();
  }, []);

  const beneficiaryOptions = useMemo(
    () => buildPaymentBeneficiaryOptions({ clients, suppliers, orders }),
    [clients, suppliers, orders],
  );

  const sourceBankOptions = useMemo(
    () => buildBankSelectOptionsFromAccounts(bankAccounts, orders.map((o) => o.banque)),
    [bankAccounts, orders],
  );

  useEffect(() => {
    if (showModal && !selectedOrder && bankAccounts.length && !formData.account_id) {
      const acct = bankAccounts[0];
      setFormData((prev) => ({
        ...prev,
        account_id: String(acct.id),
        banque: acct.bank_name || prev.banque,
      }));
    }
  }, [bankAccounts, showModal, selectedOrder, formData.account_id]);

  const loadSuppliers = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/fournisseurs');
      const raw = Array.isArray(response) ? response : response?.data || [];
      setSuppliers(raw);
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
    }
  };

  // ===== HELPER FUNCTIONS =====
  const loadBankAccounts = async () => {
    try {
      const accounts = await localApiAdapter.get('/banking/accounts');
      setBankAccounts(normalizeAndSyncBankAccounts(accounts));
    } catch (error) {
      console.error('Error loading bank accounts:', error);
      setBankAccounts([]);
    }
  };

  const loadOrders = async () => {
    setLoading(true);
    try {
      const data = await localApiAdapter.get('/banking/payment-orders');
      setOrders(sortRecordsNewestFirst((Array.isArray(data) ? data : []).map(mapPaymentOrderFromApi)));
    } catch (error) {
      console.error('Error loading payment orders:', error);
      notifyError(showNotify, error.message || p('load_error'));
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredOrderIds);
    }
  };

  const handleItemSelect = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]));
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('delete_bulk_confirm', { count: selectedItems.length }))) return;
    try {
      await Promise.all(selectedItems.map((id) => localApiAdapter.delete(`/banking/payment-orders/${id}`)));
      const deleteCount = selectedItems.length;
      setSelectedItems([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
      await loadOrders();
    } catch (error) {
      console.error('Error deleting records:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const resolvedAccountId = formData.account_id || (bankAccounts[0]?.id != null ? String(bankAccounts[0].id) : '');
      const sourceAccount = bankAccounts.find((a) => String(a.id) === String(resolvedAccountId));
      const submitData = {
        ...formData,
        account_id: resolvedAccountId,
        banque: formData.banque || sourceAccount?.bank_name || '',
      };

      const missing = [];
      if (!submitData.date) missing.push(p('label_date'));
      if (!submitData.reference?.trim()) missing.push(p('label_reference'));
      if (!submitData.beneficiaire?.trim()) missing.push(p('label_beneficiary'));
      if (!submitData.account_id) missing.push(p('label_source_account'));
      if (!submitData.beneficiary_account?.trim()) missing.push(p('label_beneficiary_account'));
      if (!submitData.montant && submitData.montant !== 0) missing.push(p('label_amount_sar'));
      if (!submitData.motif?.trim()) missing.push(p('label_reason'));
      if (missing.length) {
        notifyError(showNotify, `${p('fields_required')}: ${missing.join(', ')}`);
        return;
      }
      if (!validateSaudiIban(submitData.beneficiary_account).valid) {
        notifyError(showNotify, p('iban_invalid'));
        return;
      }

      const payload = mapPaymentOrderToApi(submitData, bankAccounts);
      if (!payload.account_id) {
        notifyError(showNotify, p('account_required'));
        return;
      }
      if (selectedOrder) {
        await localApiAdapter.put(`/banking/payment-orders/${selectedOrder.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/banking/payment-orders', payload);
        notifyCreated(showNotify, entityLabel);
      }
      await loadOrders();
      resetForm();
    } catch (error) {
      console.error('Error saving payment order:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const handleEdit = (order) => {
    setSelectedOrder(order);
    setFormData({
      ...order,
      montant: order.montant ?? '',
      account_id: order.account_id ? String(order.account_id) : '',
      beneficiary_account: order.beneficiary_account || '',
      paymentMethod: order.paymentMethod || 'sarie',
      purposeCode: order.purposeCode || '02',
      priority: order.priority || 'normal',
    });
    setOrderFormTab('generalInfo');
    setShowModal(true);
  };

  const handleView = (order) => {
    setSelectedOrder(order);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(p('delete_confirm'))) return;
    try {
      await localApiAdapter.delete(`/banking/payment-orders/${id}`);
      notifyDeleted(showNotify, entityLabel);
      await loadOrders();
    } catch (error) {
      console.error('Error deleting payment order:', error);
      notifyError(showNotify, error.message || p('delete_error'));
    }
  };

  const resetForm = () => {
    setFormData({ date: '', reference: '', beneficiaire: '', banque: '', montant: '', motif: '', dateExecution: '', status: '', account_id: '', beneficiary_account: '', paymentMethod: 'sarie', purposeCode: '02', priority: 'normal' });
    setSelectedOrder(null);
    setOrderFormTab('generalInfo');
    setShowModal(false);
  };

  const openCreateModal = () => {
    const today = new Date().toISOString().slice(0, 10);
    setSelectedOrder(null);
    setFormData({
      date: today,
      reference: '',
      beneficiaire: '',
      banque: '',
      montant: '',
      motif: '',
      dateExecution: today,
      status: 'pending',
      account_id: bankAccounts[0]?.id ? String(bankAccounts[0].id) : '',
      beneficiary_account: '',
      paymentMethod: 'sarie',
      purposeCode: '02',
      priority: 'normal',
    });
    setOrderFormTab('generalInfo');
    setShowModal(true);
  };

  const PAYMENT_ORDER_FIELD_COUNT = 14;

  const orderFormTabs = useMemo(() => resolveBankingModalFormTabs([
    { id: 'generalInfo', label: p('section_general_info'), icon: FileCheck, iconClass: 'text-emerald-400' },
    { id: 'paymentDetails', label: p('section_payment_details'), icon: Banknote, iconClass: 'text-yellow-400' },
  ], PAYMENT_ORDER_FIELD_COUNT), [p]);

  const paymentOrderStatusOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending'), icon: Clock, style: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { value: 'executed', label: p('status_executed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [p]);

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.patch(`/banking/payment-orders/${recordId}/status`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadOrders();
    } catch (error) {
      console.error('Error updating payment order status:', error);
      notifyError(showNotify, error.message || p('save_error'));
    }
  };

  const renderOrderStatusBadge = (status, recordId, { clickable = true } = {}) => (
    <QawaemClickableStatusBadge
      status={status || 'pending'}
      recordId={recordId}
      options={paymentOrderStatusOptions}
      onChange={clickable ? handleStatusChange : undefined}
      title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' }) })}
    />
  );

  const stats = {
    total: orders.length,
    pending: orders.filter(o => o.status === 'pending').length,
    executed: orders.filter(o => o.status === 'executed').length,
    cancelled: orders.filter(o => o.status === 'cancelled').length,
    totalAmount: orders.reduce((sum, o) => sum + (o.montant || 0), 0)
  };

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total_orders'), value: stats.total, icon: FileCheck, variant: 'copper' },
    { key: 'pending', label: p('stat_pending'), value: stats.pending, icon: Clock, variant: 'forest' },
    { key: 'executed', label: p('stat_executed'), value: stats.executed, icon: CheckCircle, variant: 'copper' },
    { key: 'amount', label: p('stat_total_amount'), value: formatBrandMoneyCompact(stats.totalAmount), icon: Banknote, variant: 'forest' },
  ], 4), [stats, p]);

  const formatDate = (date) => {
    if (!date) return '—';
    const d = new Date(date);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(dateLocale);
  };

  const exportHeaders = [
    { key: 'reference', label: p('col_reference') },
    { key: 'date', label: p('col_date') },
    { key: 'beneficiaire', label: p('col_beneficiary') },
    { key: 'banque', label: p('col_bank') },
    { key: 'montant', label: p('col_amount') },
    { key: 'dateExecution', label: p('col_execution') },
    { key: 'status', label: p('col_status') },
  ];

  const handleExportCSV = () => {
    exportBankingRows(filteredOrders, exportHeaders, 'ordres_paiement', 'csv');
    setShowExportDropdown(false);
  };

  const handleExportExcel = () => {
    exportBankingRows(filteredOrders, exportHeaders, 'ordres_paiement', 'excel');
    setShowExportDropdown(false);
  };

  const handleExportPDF = () => {
    exportBankingPdf(p('title'));
    setShowExportDropdown(false);
  };

  const handleImport = () => {
    importBankingRows({
      headers: exportHeaders,
      schema: {
        requiredFields: ['beneficiaire', 'date', 'montant'],
        validators: { montant: (v) => !v || !Number.isNaN(parseFloat(v)) },
      },
      mapRow: (row) => mapPaymentOrderToApi({
        reference: row.reference,
        date: row.date,
        beneficiaire: row.beneficiaire,
        banque: row.banque,
        montant: row.montant,
        dateExecution: row.dateExecution || row.date,
        status: row.status || 'pending',
        account_id: bankAccounts[0]?.id,
        beneficiary_account: SAUDI_SAMPLE_IBAN,
        motif: row.motif || row.reference || 'Import',
      }),
      postRow: (payload) => localApiAdapter.post('/banking/payment-orders', payload),
      onComplete: loadOrders,
    });
    setShowImportDropdown(false);
  };

  // Main view tabs - GPS Pattern
  const mainTabs = [
    { id: 'all_orders', nameKey: 'tab_general', icon: Layers },
    { id: 'by_status', nameKey: 'tab_by_status', icon: Shield },
    { id: 'by_bank', nameKey: 'tab_by_bank', icon: Banknote },
    { id: 'by_beneficiary', nameKey: 'tab_by_beneficiary', icon: Users }
  ];

  // Quick filter buttons - GPS Pattern
  const quickFilterButtons = [
    { id: 'all', nameKey: 'filter_all', icon: Layers },
    { id: 'pending', nameKey: 'filter_pending', icon: Clock },
    { id: 'executed', nameKey: 'filter_executed', icon: CheckCircle },
    { id: 'cancelled', nameKey: 'filter_cancelled', icon: XCircle }
  ];

  // Distribution data - GPS Pattern
  const orderDistribution = {
    all_orders: {
      titleKey: 'dist_overview',
      data: [
        { nameKey: 'dist_pending', filterKey: 'pending', count: stats.pending, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_executed', filterKey: 'executed', count: stats.executed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_cancelled', filterKey: 'cancelled', count: stats.cancelled, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { nameKey: 'dist_total_orders', filterKey: 'total', count: stats.total, color: 'bg-gradient-to-r from-blue-500 to-blue-600' }
      ]
    },
    by_status: {
      titleKey: 'dist_by_status',
      data: [
        { nameKey: 'dist_pending', filterKey: 'pending', count: stats.pending, color: 'bg-gradient-to-r from-amber-500 to-amber-600' },
        { nameKey: 'dist_executed', filterKey: 'executed', count: stats.executed, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'dist_cancelled', filterKey: 'cancelled', count: stats.cancelled, color: 'bg-gradient-to-r from-red-500 to-red-600' }
      ]
    },
    by_bank: {
      titleKey: 'dist_by_bank',
      data: [...new Set(orders.map(o => o.banque))].slice(0, 4).map((bank, idx) => ({
        name: bank,
        filterKey: bank,
        count: orders.filter(o => o.banque === bank).length,
        color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-amber-500 to-amber-600', 'bg-gradient-to-r from-green-500 to-green-600'][idx % 4]
      }))
    },
    by_beneficiary: {
      titleKey: 'dist_by_beneficiary',
      data: [...new Set(orders.map(o => o.beneficiaire))].slice(0, 4).map((ben, idx) => ({
        name: ben,
        filterKey: ben,
        count: orders.filter(o => o.beneficiaire === ben).length,
        color: ['bg-gradient-to-r from-blue-500 to-blue-600', 'bg-gradient-to-r from-purple-500 to-purple-600', 'bg-gradient-to-r from-amber-500 to-amber-600', 'bg-gradient-to-r from-green-500 to-green-600'][idx % 4]
      }))
    }
  };

  const columnStatusFilterOptions = useMemo(() => [
    { value: 'pending', label: p('status_pending', { defaultValue: 'قيد الانتظار' }) },
    { value: 'executed', label: p('status_executed', { defaultValue: 'منفّذ' }) },
    { value: 'cancelled', label: p('status_cancelled', { defaultValue: 'ملغى' }) },
  ], [p]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('reference', (row) => row.reference),
    textColumnRule('date', (row) => (row.date ? new Date(row.date).toLocaleDateString(dateLocale) : '')),
    textColumnRule('montant', (row) => String(row.montant ?? '')),
    textColumnRule('dateExecution', (row) => (row.dateExecution ? new Date(row.dateExecution).toLocaleDateString(dateLocale) : '')),
    exactColumnRule('status', (row) => row.status),
  ], [dateLocale]);

  const distributionInfo = orderDistribution[mainViewMode] || orderDistribution.all_orders;

  // Filter orders
  const baseFilteredOrders = orders.filter(order => {
    // Quick filter
    if (quickFilter === 'pending' && order.status !== 'pending') return false;
    if (quickFilter === 'executed' && order.status !== 'executed') return false;
    if (quickFilter === 'cancelled' && order.status !== 'cancelled') return false;
    
    const matchesSearch = viewMode !== 'cards' || !searchTerm ||
      order.reference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.beneficiaire?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesSearch;
  });

  const filteredOrders = applyColumnFilters(baseFilteredOrders, columnFilters, columnFilterRules)
    .sort((a, b) => b.id - a.id);

  const pagedFilteredOrders = useMemo(
    () => sliceBanqueListItems(filteredOrders, bankingConfig.itemsPerPage),
    [filteredOrders, bankingConfig.itemsPerPage],
  );

  const filteredOrderIds = useMemo(() => pagedFilteredOrders.map((o) => o.id), [pagedFilteredOrders]);
  const allRowsSelected = filteredOrderIds.length > 0 && filteredOrderIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  const qawaemColumnDefs = useMemo(() => [
    { key: 'reference', label: p('col_reference'), checked: columnVisibility.reference !== false },
    { key: 'date', label: p('col_date'), checked: columnVisibility.date !== false },
    { key: 'beneficiaire', label: p('col_beneficiary'), checked: columnVisibility.beneficiaire !== false },
    { key: 'banque', label: p('col_bank'), checked: columnVisibility.banque !== false },
    { key: 'montant', label: p('col_amount'), checked: columnVisibility.montant !== false },
    { key: 'dateExecution', label: p('col_execution'), checked: columnVisibility.dateExecution !== false },
    { key: 'actions', label: p('col_actions'), checked: true, required: true },
  ], [columnVisibility, p]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['reference', 'date', 'beneficiaire', 'banque', 'montant', 'dateExecution'].filter((k) => isColVisible(k)).length + 1;

  // ===== RENDER =====
  return (
    <QawaemErpListPageShell
      className="space-y-3"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={FileCheck}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedItems.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={t('common.delete', { defaultValue: 'حذف' })}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedItems.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openCreateModal}
      createLabel={p('new_order')}
      headerActions={(
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
        >
          <Filter className="h-5 w-5" />
          <span className="hidden sm:inline">{p('advanced_options')}</span>
          {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: t('common.import', { defaultValue: 'استيراد' }),
        exportLabel: t('common.export', { defaultValue: 'تصدير' }),
        onImportCSV: handleImport,
        onImportExcel: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >
      {/* Advanced Options Section - GPS Pattern */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>
          
          {mainViewMode === 'all_orders' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <X size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>{distributionInfo.data.map((item) => {
                const itemKey = item.filterKey || item.nameKey;
                const itemLabel = item.nameKey ? p(item.nameKey) : item.name;
                return (
                <button key={itemKey} onClick={() => setActiveDistributionFilter(activeDistributionFilter === itemKey ? null : itemKey)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === itemKey ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === itemKey ? 'text-white' : 'text-white/80'}`}>{itemLabel}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === itemKey ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: (() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                  })() })}</p>
                </button>
              );})}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Main Content with Integrated Header - GPS Pattern */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' && (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('reference') ? (
                    <TableColumnHeaderFilter columnKey="reference" label={p('col_reference')} value={columnFilters.reference || ''} onChange={(v) => setFilter('reference', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('date') ? (
                    <TableColumnHeaderFilter columnKey="date" label={p('col_date')} value={columnFilters.date || ''} onChange={(v) => setFilter('date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('beneficiaire') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_beneficiary')}</th>
                  ) : null}
                  {isColVisible('banque') ? (
                    <th className="px-6 py-4 text-left text-white font-semibold">{p('col_bank')}</th>
                  ) : null}
                  {isColVisible('montant') ? (
                    <TableColumnHeaderFilter columnKey="montant" label={p('col_amount')} value={columnFilters.montant || ''} onChange={(v) => setFilter('montant', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  {isColVisible('dateExecution') ? (
                    <TableColumnHeaderFilter columnKey="dateExecution" label={p('col_execution')} value={columnFilters.dateExecution || ''} onChange={(v) => setFilter('dateExecution', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white py-4" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {pagedFilteredOrders.length > 0 ? (
                  pagedFilteredOrders.map((order, index) => (
                    <tr key={order.id} data-testid={`payment-order-row-${order.id}`} data-reference={order.reference} className={`hover:bg-white/5 transition-colors ${selectedItems.includes(order.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(order.id)}
                            onChange={() => handleItemSelect(order.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('reference') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{order.reference}</span></td>
                      ) : null}
                      {isColVisible('date') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{formatDate(order.date)}</span></td>
                      ) : null}
                      {isColVisible('beneficiaire') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{order.beneficiaire}</span></td>
                      ) : null}
                      {isColVisible('banque') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{order.banque}</span></td>
                      ) : null}
                      {isColVisible('montant') ? (
                        <td className="px-6 py-4"><span className="text-white font-bold text-lg">{fmt(order.montant)}</span></td>
                      ) : null}
                      {isColVisible('dateExecution') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{formatDate(order.dateExecution)}</span></td>
                      ) : null}
                      <td className="px-6 py-4 text-center align-middle">
                        <div className="flex items-center justify-center gap-2" data-testid={`payment-order-status-cell-${order.id}`}>
                          {renderOrderStatusBadge(order.status, order.id)}
                          <QawaemErpRowActions
                            onView={() => handleView(order)}
                            onEdit={() => handleEdit(order)}
                            onDelete={() => handleDelete(order.id)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><FileCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_no_orders')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_orders_total', { count: filteredOrders.length })}</span>
                      <span>{p('footer_total_amount')} <span className="text-white font-semibold">{fmt(filteredOrders.reduce((sum, o) => sum + (o.montant || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {/* Card View - GPS Pattern - 4-Column Grid with Longer Horizontal Cards */}
        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {pagedFilteredOrders.length > 0 ? (
              pagedFilteredOrders.map((order, index) => (
                <motion.div key={order.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><FileCheck size={20} className="text-emerald-400" />{order.reference}</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_general_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_reference')}:</span> {order.reference}</div>
                            <div><span className="font-medium">{p('label_date')}:</span> {formatDate(order.date)}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_beneficiary_bank')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_beneficiary')}:</span> {order.beneficiaire}</div>
                            <div><span className="font-medium">{p('label_bank')}:</span> {order.banque}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_amount')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="text-white font-bold text-lg">{fmt(order.montant)}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('section_execution')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_execution_date')}:</span> {formatDate(order.dateExecution)}</div>
                            <div><span className="font-medium">{p('label_reason')}:</span> {order.motif}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      {renderOrderStatusBadge(order.status, order.id)}
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(order)}
                        onEdit={() => handleEdit(order)}
                        onDelete={() => handleDelete(order.id)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><FileCheck size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_no_orders')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && selectedOrder ? (
        ReactDOM.createPortal(
          <AnimatePresence>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" onClick={() => setShowViewModal(false)}>
              <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.2 }} onClick={(e) => e.stopPropagation()} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '1100px' }}>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">{p('modal_view_title')}</h2>
                  <button type="button" onClick={() => setShowViewModal(false)} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
                </div>
                <div className="bg-white/5 rounded-xl p-5 border border-white/20">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><p className="text-white/60 text-sm mb-1">{p('label_reference')}</p><p className="text-white font-medium">{selectedOrder.reference}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_date')}</p><p className="text-white font-medium">{formatDate(selectedOrder.date)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('col_status')}</p>{renderOrderStatusBadge(selectedOrder.status, selectedOrder.id, { clickable: false })}</div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_beneficiary')}</p><p className="text-white font-medium">{selectedOrder.beneficiaire}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_bank')}</p><p className="text-white font-medium">{selectedOrder.banque}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_reason')}</p><p className="text-white font-medium">{selectedOrder.motif}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_amount')}</p><p className="text-green-400 font-bold text-xl">{fmt(selectedOrder.montant)}</p></div>
                    <div><p className="text-white/60 text-sm mb-1">{p('label_execution_date_full')}</p><p className="text-white font-medium">{formatDate(selectedOrder.dateExecution)}</p></div>
                    {selectedOrder.beneficiary_account ? (
                      <div><p className="text-white/60 text-sm mb-1">{p('label_beneficiary_account')}</p><p className="text-white font-medium font-mono text-sm">{selectedOrder.beneficiary_account}</p></div>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )
      ) : null}

      {/* Add/Edit Modal — tabs (Products.js pattern) */}
      {showModal && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4" style={{ top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden" style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '90%', maxWidth: '1200px' }}>
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white">{selectedOrder ? p('modal_edit_title') : p('modal_new_title')}</h2>
              <div className="flex items-center gap-3">
                <button type="submit" form="order-form" data-testid="payment-order-submit" className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30"><CheckCircle size={18} />{selectedOrder ? p('btn_update') : p('btn_create')}</button>
                <button onClick={resetForm} className="text-white/70 hover:text-white transition-colors"><X size={24} /></button>
              </div>
            </div>
            <div className="shrink-0 px-6 pt-3 border-b border-white/10">
              <div className="flex flex-wrap gap-1" role="tablist" aria-label={selectedOrder ? p('modal_edit_title') : p('modal_new_title')}>
                {orderFormTabs.map((tab) => {
                  const TabIcon = tab.icon;
                  const active = orderFormTab === tab.id;
                  return (
                    <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setOrderFormTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                      <TabIcon size={16} className={active ? tab.iconClass : ''} />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="overflow-y-auto p-6 flex-1 min-h-0">
              <form id="order-form" data-testid="payment-order-form" noValidate onSubmit={handleSubmit}>
                {orderFormTab === 'generalInfo' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_date')} <span className="qawaem-required-mark">*</span></label><CustomDatePicker data-testid="payment-order-date" value={formData.date} onChange={(dateString) => setFormData({ ...formData, date: dateString })} placeholder={p('placeholder_select_date')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')} <span className="qawaem-required-mark">*</span></label><input type="text" data-testid="payment-order-reference" required value={formData.reference} onChange={(e) => setFormData({ ...formData, reference: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_reference')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_execution_date_full')} <span className="qawaem-required-mark">*</span></label><CustomDatePicker data-testid="payment-order-execution-date" value={formData.dateExecution} onChange={(dateString) => setFormData({ ...formData, dateExecution: dateString })} placeholder={p('placeholder_select_date')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_status')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="payment-order-status" value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={[
                      { value: 'pending', label: p('status_pending'), icon: <Clock size={14} className="text-amber-400" />, isDefault: true },
                      { value: 'executed', label: p('status_executed'), icon: <CheckCircle size={14} className="text-green-400" />, isDefault: true },
                      { value: 'cancelled', label: p('status_cancelled'), icon: <XCircle size={14} className="text-red-400" />, isDefault: true }
                    ]} searchable={false} creatable={false} autoSelect={false} placeholder={p('placeholder_select_status')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_source_account')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="payment-order-account" value={String(formData.account_id || '')} onChange={(value) => { const acct = bankAccounts.find((a) => String(a.id) === String(value)); setFormData({ ...formData, account_id: value, banque: acct?.bank_name || formData.banque }); }} options={bankAccounts.map((a) => ({ value: String(a.id), label: `${a.bank_name} — ${a.account_name}`, isDefault: true }))} searchable={true} creatable={false} autoSelect={false} placeholder={p('placeholder_type_select')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_bank')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="payment-order-bank" value={formData.banque} onChange={(value) => setFormData({ ...formData, banque: value })} options={sourceBankOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_type_select')} /></div>
                  </div>
                )}
                {orderFormTab === 'paymentDetails' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_beneficiary')} <span className="qawaem-required-mark">*</span></label><CustomSelect data-testid="payment-order-beneficiary" value={formData.beneficiaire} onChange={(value) => setFormData({ ...formData, beneficiaire: value })} options={beneficiaryOptions} searchable={true} creatable={true} autoSelect={false} placeholder={p('placeholder_type_select')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_beneficiary_account')} <span className="qawaem-required-mark">*</span></label><input type="text" data-testid="payment-order-beneficiary-account" required value={formData.beneficiary_account} onChange={(e) => setFormData({ ...formData, beneficiary_account: e.target.value.toUpperCase().replace(/\s/g, '') })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono text-sm" placeholder={p('placeholder_iban')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_amount_sar')} <span className="qawaem-required-mark">*</span></label><CustomNumberInput data-testid="payment-order-amount" step={0.01} value={formData.montant} onChange={(value) => setFormData({ ...formData, montant: value })} placeholder="0.00" /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reason')} <span className="qawaem-required-mark">*</span></label><input type="text" data-testid="payment-order-motif" required value={formData.motif} onChange={(e) => setFormData({ ...formData, motif: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" placeholder={p('placeholder_reason')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_payment_method')}</label><CustomSelect data-testid="payment-order-method" value={formData.paymentMethod} onChange={(value) => setFormData({ ...formData, paymentMethod: value })} options={SAUDI_PAYMENT_ORDER_METHODS.map((m) => ({ value: m.value, label: p(m.labelKey), isDefault: true }))} searchable={false} creatable={false} autoSelect={false} placeholder={p('placeholder_type_select')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_purpose_code')}</label><CustomSelect data-testid="payment-order-purpose" value={formData.purposeCode} onChange={(value) => setFormData({ ...formData, purposeCode: value })} options={SAUDI_PURPOSE_CODES.map((c) => ({ value: c.value, label: p(c.labelKey), isDefault: true }))} searchable={true} creatable={false} autoSelect={false} placeholder={p('placeholder_type_select')} /></div>
                    <div><label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_priority')}</label><CustomSelect data-testid="payment-order-priority" value={formData.priority} onChange={(value) => setFormData({ ...formData, priority: value })} options={SAUDI_PAYMENT_PRIORITIES.map((pr) => ({ value: pr.value, label: p(pr.labelKey), isDefault: true }))} searchable={false} creatable={false} autoSelect={false} placeholder={p('placeholder_type_select')} /></div>
                  </div>
                )}
              </form>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default PaymentOrdersPage;


